import { http } from '@pendo/http';

// Bulk-user job endpoints share a {subscriptionId}:{userId}:{suffix} requestId
// and a /<endpoint>/status/<requestId> response with `status: 'completed' |
// 'failed' | 'in_progress'`. Operations bind endpoint + suffix + label here.

export const DEFAULT_POLL_INTERVAL_MS = 5000;

export const DEFAULT_POLL_MAX_ATTEMPTS = 240;

export function buildBulkUserJobRequestId ({ subscriptionId, userId, suffix }) {
    return `${subscriptionId}:${userId}:${suffix}`;
}

export async function getBulkUserJobStatus ({ endpoint, requestId }) {
    try {
        const res = await http.get(`${endpoint}/status/${requestId}`);

        return res.data;
    } catch (err) {
        if (err?.response?.status === 404) {
            return { status: 'none' };
        }
        throw err;
    }
}

export function pollBulkUserJobStatus ({
    endpoint,
    requestId,
    intervalMs = DEFAULT_POLL_INTERVAL_MS,
    maxAttempts = DEFAULT_POLL_MAX_ATTEMPTS,
    signal,
    onInProgress,
    operationLabel = 'Bulk user job',
} = {}) {
    return new Promise((resolve, reject) => {
        let attempts = 0;
        let timeoutId = null;

        function cleanup () {
            if (timeoutId !== null) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }
            if (signal) {
                signal.removeEventListener('abort', onAbort);
            }
        }

        function onAbort () {
            cleanup();
            const reason = signal?.reason || new Error(`${operationLabel} polling aborted`);
            reject(reason);
        }

        if (signal) {
            if (signal.aborted) {
                onAbort();

                return;
            }
            signal.addEventListener('abort', onAbort, { once: true });
        }

        const tick = async () => {
            if (signal?.aborted) {
                onAbort();

                return;
            }

            attempts += 1;

            try {
                const res = await http.get(`${endpoint}/status/${requestId}`);

                if (signal?.aborted) {
                    cleanup();

                    return;
                }

                const body = res?.data || {};

                if (body.status === 'completed') {
                    cleanup();
                    resolve({ status: 'completed', results: body.results || [] });

                    return;
                }

                if (body.status === 'failed') {
                    cleanup();
                    resolve({ status: 'failed', error: body.error || `${operationLabel} failed` });

                    return;
                }

                if (body.status === 'in_progress' && typeof onInProgress === 'function') {
                    onInProgress();
                }

                if (attempts >= maxAttempts) {
                    cleanup();
                    reject(new Error(`${operationLabel} polling exceeded max attempts`));

                    return;
                }

                timeoutId = setTimeout(tick, intervalMs);
            } catch (err) {
                cleanup();
                reject(err);
            }
        };

        tick();
    });
}
